import { useRouter } from 'next/router'
import ErrorPage from 'next/error'
import Head from 'next/head'
import Link from 'next/link'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { format } from 'date-fns'
import BlogPost from '@interfaces/post'
import { getAllBlogPages, getBlogPageBySlug } from '@lib/api'
import markdownToHtml from '@lib/markdownToHtml'
import Layout from '@comp/layout'
import Seo from '@comp/seo'
import JsonLd from '@comp/structured-data'
import FinalCta from '@comp/home/final-cta'
import ProseArticle from '@comp/content/prose-article'
import ProductCta from '@comp/blog/product-cta'
import RelatedArticles from '@comp/blog/related-articles'
import { relatedProductFor, RelatedProduct } from '@lib/related-product'
import Container from '@comp/ui/container'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import { blogCategories } from '../../blog.config'

const EASE = [0.22, 1, 0.36, 1] as const

type Props = {
  post: BlogPost
  relatedPosts: BlogPost[]
  relatedProduct: RelatedProduct
  locale: string
}

export default function BlogDetail({
  post,
  relatedPosts,
  relatedProduct,
  locale,
}: Props) {
  const router = useRouter()
  const title = `${post?.title ?? ''} | Monofor`

  if (!router.isFallback && !post?.slug) {
    return <ErrorPage statusCode={404} />
  }
  if (post === undefined) {
    return <ErrorPage statusCode={404} />
  }

  const postDateString = post.date
    ? format(new Date(post.date), 'd MMM yyyy')
    : ''
  const updatedDateString = post.updated
    ? format(new Date(post.updated), 'd MMM yyyy')
    : ''
  const tags: string[] = post.tags ? post.tags.split(', ') : []
  const category =
    post.category &&
    blogCategories.find(({ slug }) => slug === post.category)?.title

  return (
    <Layout>
      <Seo
        title={title}
        description={post.summary ?? ''}
        path={`${router.locale === 'tr' ? '/tr' : ''}/blog/${post.slug}`}
        ogImage={post.image}
        ogType="article"
      />
      <Head>
        {post.date && (
          <meta property="article:published_time" content={post.date} />
        )}
        {(post.updated || post.date) && (
          <meta
            property="article:modified_time"
            content={post.updated || post.date}
          />
        )}
        {tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
      </Head>
      <JsonLd
        id="article"
        data={{
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.summary,
          ...(post.image ? { image: post.image } : {}),
          ...(post.date
            ? {
                datePublished: post.date,
                dateModified: post.updated || post.date,
              }
            : {}),
          inLanguage: locale,
          ...(post.tags ? { keywords: post.tags } : {}),
          author: { '@type': 'Organization', name: 'Monofor' },
          publisher: {
            '@type': 'Organization',
            name: 'Monofor',
            logo: {
              '@type': 'ImageObject',
              url: 'https://monofor.com/android-chrome-512x512.png',
            },
          },
          mainEntityOfPage: `https://monofor.com${
            locale === 'tr' ? '/tr' : ''
          }/blog/${post.slug}`,
        }}
      />
      <main className="bg-surface text-ink-primary">
        <section className="bg-surface-subtle border-b border-line-subtle pt-16 pb-12 lg:pt-24 lg:pb-16">
          <Container size="md">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
            >
              <Link
                href="/blog"
                className="inline-flex items-center gap-2 text-body-sm text-ink-tertiary transition hover:text-ink-primary"
              >
                <span aria-hidden>←</span>
                Back to blog
              </Link>
            </motion.div>

            <motion.div
              className="mt-8 flex flex-wrap items-center gap-2 text-caption font-semibold uppercase tracking-[0.12em]"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE, delay: 0.1 }}
            >
              {category && <span className="text-brand-700">{category}</span>}
              {postDateString && (
                <>
                  {category && <span className="text-ink-muted">·</span>}
                  <span className="text-ink-tertiary">{postDateString}</span>
                </>
              )}
              {post.read_time && (
                <>
                  <span className="text-ink-muted">·</span>
                  <span className="text-ink-tertiary">
                    {post.read_time} min read
                  </span>
                </>
              )}
              {updatedDateString && (
                <>
                  <span className="text-ink-muted">·</span>
                  <span className="text-accent-700">
                    {router.locale === 'tr' ? 'Güncellendi: ' : 'Updated '}
                    {updatedDateString}
                  </span>
                </>
              )}
            </motion.div>

            <motion.div
              className="mt-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.08 }}
            >
              <Heading as="h1" size="display-xl">
                {post.title}
              </Heading>
            </motion.div>

            {post.summary && (
              <motion.div
                className="mt-6 max-w-2xl"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: EASE, delay: 0.15 }}
              >
                <Text size="xl" tone="secondary">
                  {post.summary}
                </Text>
              </motion.div>
            )}
          </Container>
        </section>

        {post.image && (
          <Container size="lg" className="-mt-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.18 }}
              className="overflow-hidden rounded-3xl border border-line-subtle bg-surface-subtle"
            >
              <Image
                src={post.image}
                alt={post.title}
                width={1200}
                height={630}
                priority
                sizes="(max-width: 1024px) 100vw, 1024px"
                className="h-auto w-full"
              />
            </motion.div>
          </Container>
        )}

        <article className="bg-surface pt-16 pb-24 lg:pt-20 lg:pb-32">
          <Container size="md">
            <ProseArticle html={post.content} />
            {tags.length > 0 && (
              <div className="mt-16 flex flex-wrap items-center gap-2 border-t border-line-subtle pt-8">
                <span className="text-eyebrow font-semibold uppercase text-ink-tertiary">
                  Tags
                </span>
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-pill bg-surface-subtle px-3 py-1 text-body-sm text-ink-secondary"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <ProductCta product={relatedProduct} />
          </Container>
        </article>

        <RelatedArticles locale={locale} posts={relatedPosts} />
        <FinalCta />
      </main>
    </Layout>
  )
}

type Params = {
  params: { slug: string }
  locale: string
}

export async function getStaticProps({ params, locale }: Params) {
  const post = getBlogPageBySlug(locale, params.slug, [
    'title',
    'date',
    'updated',
    'slug',
    'content',
    'summary',
    'tags',
    'ogImage',
    'image',
    'coverImage',
    'category',
    'read_time',
  ])
  const content = await markdownToHtml(post.content || '')

  const listFields = [
    'title',
    'date',
    'slug',
    'summary',
    'image',
    'category',
    'read_time',
    'tags',
  ]
  const allPosts = getAllBlogPages(locale, listFields).filter(
    (p) => p.slug !== post.slug
  )
  // Same-category posts first, newest first; pad with the most recent others.
  const sameCategory = allPosts.filter((p) => p.category === post.category)
  const others = allPosts.filter((p) => p.category !== post.category)
  const relatedPosts = [...sameCategory, ...others].slice(0, 3)

  return {
    props: {
      post: {
        ...post,
        content,
      },
      relatedPosts,
      relatedProduct: relatedProductFor(post),
      locale,
    },
  }
}

export async function getStaticPaths() {
  const turkishPosts = getAllBlogPages('tr', ['slug'])
  const englishPosts = getAllBlogPages('en', ['slug'])

  const turkishPaths = turkishPosts.map((post) => ({
    params: { slug: post.slug },
    locale: 'tr',
  }))
  const englishPaths = englishPosts.map((post) => ({
    params: { slug: post.slug },
    locale: 'en',
  }))

  return {
    paths: turkishPaths.concat(englishPaths),
    fallback: false,
  }
}
